import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useAtomValue, useUpdateAtom } from "jotai/utils";
import React, { useEffect, useState } from "react";
import { MenuContent, MenuItem } from "../components/Menu";
import { blocksRefAtom, selectionAtom } from "../jotai/atoms";
import { createBlockObj } from "../liveblocks/maps";
import { useUpdatePresence } from "../liveblocks/presences";
import { useBlockAction } from "../liveblocks/useBlockAction";
import { styled } from "../stitches.config";
import { AnyBlock } from "../types";
import { mouseTarget } from "../ui/selection/usePreview";
import { canvasPos } from "../utils/canvasPos";
import { nextLowest } from "../utils/updatedAt";

export const BlockContextMenu = React.memo(function BlockContextMenu({}) {
  const { createBlock, deleteBlock, updateBlock, duplicateBlock } =
    useBlockAction();

  const blocksRef = useAtomValue(blocksRefAtom);
  const setSelection = useUpdateAtom(selectionAtom);
  const [target, setTarget] = useState<{
    block: AnyBlock;
    event: MouseEvent;
  } | null>(null);

  const updatePresence = useUpdatePresence();

  useEffect(() => {
    function onContextMenu(event: MouseEvent) {
      const block = mouseTarget(event, blocksRef.current);
      if (!block) {
        setTarget(null);
        return;
      }

      event.preventDefault();

      setSelection((s) => ({ ...s, [block.id]: Date.now() }));
      setTarget({ block, event });
    }

    document.addEventListener("contextmenu", onContextMenu);
    return () => {
      document.removeEventListener("contextmenu", onContextMenu);
    };
  }, [blocksRef, setSelection, updatePresence]);

  useEffect(() => {
    if (target) {
      const position = canvasPos(target.event);

      updatePresence({
        contextMenu: {
          type: "block",
          position,
          hoverIndex: -1,
        },
      });
    } else {
      updatePresence({ contextMenu: null });
    }
  }, [target, updatePresence]);

  if (!target) {
    return null;
  }

  const { block, event } = target;

  function onDelete() {
    deleteBlock(block.id);
  }

  function onLockToggle() {
    updateBlock({ id: block.id, locked: !block.locked });
  }

  function onSendToBack() {
    const updatedAt = nextLowest(blocksRef.current);
    updateBlock({ id: block.id, updatedAt });
  }

  function onDuplicate() {
    duplicateBlock(block);
  }

  function onStyleBlock() {
    const style = createStyleBlock(block);
    createBlock(style);
  }

  function createHoverHandler(hoverIndex: number) {
    const position = canvasPos(event);

    return function onHover() {
      updatePresence({
        contextMenu: {
          type: "block",
          hoverIndex,
          position,
        },
      });
    };
  }

  const style = {
    left: target.event.clientX,
    top: target.event.clientY,
  };

  return (
    /* @ts-ignore https://github.com/DefinitelyTyped/DefinitelyTyped/issues/46691 */
    <DropdownMenu.Root
      open={!!target}
      defaultOpen={!!target}
      onOpenChange={() => setTarget(null)}
    >
      <Trigger style={style} />
      <MenuContent disableOutsideScroll={false}>
        <MenuItem onMouseOver={createHoverHandler(0)} onSelect={onDelete}>
          Delete
        </MenuItem>
        <MenuItem onMouseOver={createHoverHandler(1)} onSelect={onDuplicate}>
          Duplicate
        </MenuItem>
        <MenuItem onMouseOver={createHoverHandler(2)} onSelect={onSendToBack}>
          Send to back
        </MenuItem>
        <MenuItem onMouseOver={createHoverHandler(3)} onSelect={onStyleBlock}>
          Style block
        </MenuItem>
        <MenuItem onMouseOver={createHoverHandler(4)} onSelect={onLockToggle}>
          {block.locked ? "Unlock" : "Lock"}
        </MenuItem>
      </MenuContent>
    </DropdownMenu.Root>
  );
});

function createStyleBlock(block: AnyBlock) {
  const elementId = "block-" + block.id;

  const styleBlock = createBlockObj({
    type: "css",
    position: {
      x: block.position.x + block.size.width + 1,
      y: block.position.y,
    },
    size: {
      width: 16,
      height: 14,
    },
    value: `/* write some css! */
#${elementId} {
  background-color: magenta;
}`,
  });

  return styleBlock;
}

const Trigger = styled(DropdownMenu.Trigger, {
  position: "fixed",
  opacity: 0,
});
